/**
 * Emergency "classic login" escape hatch.
 *
 * When Keycloak/SSO is configured the login screen auto-redirects straight to
 * Keycloak (see `LoginComponent`). This override forces the classic CURO
 * username/password form instead, so you can still sign in when Keycloak is
 * down or misconfigured.
 *
 * Trigger it either way:
 *  - Visit the login URL with `?local=1` — works in the hash route too, e.g.
 *    `/#/login?local=1`. This persists a cookie so it survives the redirects
 *    that follow. Turn it back off with `?local=0` (or `off`/`false`/`no`).
 *  - Or set the cookie by hand (DevTools ▸ Application ▸ Cookies):
 *    `mypayter-local-login = enabled`.
 *
 * This is only a *view* switch deciding which login form renders — it is NOT a
 * security boundary: the classic form still requires valid CURO credentials.
 */

const LOCAL_LOGIN_COOKIE = 'mypayter-local-login'
const LOCAL_LOGIN_ON = 'enabled'

/** `on` for truthy `?local` values, `off` for explicit disables, else null. */
function readLocalParam(): 'on' | 'off' | null {
  // Hash routing keeps params in the fragment, so scan both search and hash.
  const haystack = `${window.location.search}&${window.location.hash}`
  const match = haystack.match(/[?&#]local=([^&#]*)/)
  if (!match) {
    return null
  }
  const value = decodeURIComponent(match[1]).toLowerCase()
  return /^(0|off|false|no)$/.test(value) ? 'off' : 'on'
}

function hasLocalLoginCookie(): boolean {
  return document.cookie
    .split(';')
    .map((entry) => entry.trim())
    .some((entry) => entry === `${LOCAL_LOGIN_COOKIE}=${LOCAL_LOGIN_ON}`)
}

function writeCookie(enabled: boolean): void {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = enabled
    ? `${LOCAL_LOGIN_COOKIE}=${LOCAL_LOGIN_ON}; path=/; SameSite=Lax${secure}`
    : `${LOCAL_LOGIN_COOKIE}=; path=/; Max-Age=0; SameSite=Lax${secure}`
}

/**
 * Whether the classic login form should be forced instead of auto-redirecting
 * to Keycloak. A `?local=` param takes precedence (and is persisted to the
 * cookie); otherwise the cookie alone decides.
 */
export function resolveLocalLogin(): boolean {
  const param = readLocalParam()
  if (param === 'on') {
    writeCookie(true)
    return true
  }
  if (param === 'off') {
    writeCookie(false)
    return false
  }
  return hasLocalLoginCookie()
}
